import React, {useCallback, useEffect} from 'react';
import styled from 'styled-components';
import {FilterDrawerSearchableSelect} from './FilterDrawerSearchableSelect';
import {FilterDrawerDateRange} from './FilterDrawerDateRange';
import {FilterDrawerSavedFilters} from './FilterDrawerSavedFilters';
import {useFilterOptions} from '../hooks/useFilterOptions';
import {useSavedFilters} from '../hooks/useSavedFilters';
import {STATUS_OPTIONS, PRIORITY_OPTIONS, SLA_OPTIONS} from '../constants/filterOptions';
import type {DrawerFilterState} from '../hooks/useFilterDrawer';
import type {FilterOptionItem} from '../hooks/useFilterOptions';

const Backdrop = styled.div`
	position: fixed;
	inset: 0;
	z-index: 40;
	background: rgb(0 0 0 / 0.3);
`;

const Panel = styled.aside`
	position: fixed;
	right: 0;
	top: 0;
	z-index: 50;
	display: flex;
	height: 100%;
	width: 320px;
	flex-direction: column;
	background: #ffffff;
	box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1);
`;

const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	border-bottom: 1px solid #e2e8f0;
	padding: 12px 16px;
`;

const Title = styled.h2`
	margin: 0;
	font-size: 16px;
	line-height: 24px;
	font-weight: 600;
	color: #0f172a;
`;

const CloseButton = styled.button`
	border: 0;
	border-radius: 6px;
	background: transparent;
	padding: 4px;
	color: #94a3b8;
	cursor: pointer;
	&:hover {
		background: #f1f5f9;
		color: #475569;
	}
`;

const CloseIcon = styled.svg`
	height: 20px;
	width: 20px;
`;

const Body = styled.div`
	flex: 1 1 0%;
	overflow-y: auto;
	padding: 16px;
	> * + * {
		margin-top: 16px;
	}
`;

const Footer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	border-top: 1px solid #e2e8f0;
	padding: 12px 16px;
`;

const ClearButton = styled.button`
	border: 0;
	background: transparent;
	padding: 0;
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 500;
	color: #64748b;
	cursor: pointer;
	&:hover {
		color: #334155;
	}
`;

const ApplyButton = styled.button`
	border: 0;
	border-radius: 6px;
	background: #4f46e5;
	padding: 8px 16px;
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 500;
	color: #ffffff;
	cursor: pointer;
	&:hover {
		background: #4338ca;
	}
	&:focus {
		outline: none;
		box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px #6366f1;
	}
`;

interface Props {
	isOpen: boolean;
	draft: DrawerFilterState;
	onClose: () => void;
	onApply: (filters: DrawerFilterState) => void;
	onUpdateDraft: (partial: Partial<DrawerFilterState>) => void;
	onClearDraft: () => void;
	savedFiltersFetchKey?: number;
	onSavedFiltersRefetch?: () => void;
	canFilterByAssignee?: boolean;
}

const statusFilterOptions: FilterOptionItem[] = STATUS_OPTIONS.map(o => ({
	id: o.id,
	label: o.label,
}));

const priorityFilterOptions: FilterOptionItem[] = PRIORITY_OPTIONS.map(o => ({
	id: o.id,
	label: o.label,
}));

const slaFilterOptions: FilterOptionItem[] = SLA_OPTIONS.map(o => ({
	id: o.id,
	label: o.label,
}));

export function FilterDrawer({
	isOpen,
	draft,
	onClose,
	onApply,
	onUpdateDraft,
	onClearDraft,
	savedFiltersFetchKey,
	onSavedFiltersRefetch,
	canFilterByAssignee = true,
}: Props) {
	useEffect(() => {
		if (!isOpen) return;
		function handleKeyDown(e: KeyboardEvent) {
			if (e.key === 'Escape') onClose();
		}
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, onClose]);

	const {labelOptions, requesterOptions, assigneeOptions} = useFilterOptions();
	const {filters: savedFilters, createFilter, deleteFilter} = useSavedFilters(savedFiltersFetchKey);

	const handleSave = useCallback(
		async (name: string) => {
			await createFilter(name, draft as unknown as Record<string, unknown>);
			onSavedFiltersRefetch?.();
		},
		[createFilter, draft, onSavedFiltersRefetch]
	);

	const handleDeleteSaved = useCallback(
		async (id: string) => {
			await deleteFilter(id);
			onSavedFiltersRefetch?.();
		},
		[deleteFilter, onSavedFiltersRefetch]
	);

	const handleLoadSaved = useCallback(
		(criteria: Partial<DrawerFilterState>) => {
			onUpdateDraft(criteria);
		},
		[onUpdateDraft]
	);

	if (!isOpen) return null;

	return (
		<>
			<Backdrop onClick={onClose} aria-hidden="true" />

			<Panel role="dialog" aria-label="Filter drawer">
				<Header>
					<Title>Filters</Title>
					<CloseButton type="button" onClick={onClose} aria-label="Close filters">
						<CloseIcon fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
							<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
						</CloseIcon>
					</CloseButton>
				</Header>

				<Body>
					<FilterDrawerSavedFilters
						filters={savedFilters}
						onLoad={handleLoadSaved}
						onSave={handleSave}
						onDelete={handleDeleteSaved}
					/>

					<FilterDrawerSearchableSelect
						label="Status"
						options={statusFilterOptions}
						selected={draft.status}
						onChange={status => onUpdateDraft({status})}
					/>

					<FilterDrawerSearchableSelect
						label="Priority"
						options={priorityFilterOptions}
						selected={draft.priority}
						onChange={priority => onUpdateDraft({priority})}
					/>

					<FilterDrawerSearchableSelect
						label="SLA"
						options={slaFilterOptions}
						selected={draft.sla_status}
						onChange={sla_status => onUpdateDraft({sla_status})}
					/>

					{canFilterByAssignee && (
						<FilterDrawerSearchableSelect
							label="Assignee"
							options={assigneeOptions}
							selected={draft.assignee_id}
							onChange={assignee_id => onUpdateDraft({assignee_id})}
						/>
					)}

					<FilterDrawerSearchableSelect
						label="Client / Requester"
						options={requesterOptions}
						selected={draft.requester_id}
						onChange={requester_id => onUpdateDraft({requester_id})}
					/>

					<FilterDrawerSearchableSelect
						label="Label"
						options={labelOptions}
						selected={draft.label_id}
						onChange={label_id => onUpdateDraft({label_id})}
					/>

					<FilterDrawerDateRange
						label="Created Date"
						from={draft.created_after}
						to={draft.created_before}
						onChangeFrom={created_after => onUpdateDraft({created_after})}
						onChangeTo={created_before => onUpdateDraft({created_before})}
					/>

					<FilterDrawerDateRange
						label="Last Updated"
						from={draft.updated_after}
						to={draft.updated_before}
						onChangeFrom={updated_after => onUpdateDraft({updated_after})}
						onChangeTo={updated_before => onUpdateDraft({updated_before})}
					/>
				</Body>

				<Footer>
					<ClearButton type="button" onClick={onClearDraft}>
						Clear all
					</ClearButton>
					<ApplyButton type="button" onClick={() => onApply(draft)}>
						Apply
					</ApplyButton>
				</Footer>
			</Panel>
		</>
	);
}
